<template>
  <div class="esim-container">
    <!-- 確認視窗 -->
    <div v-if="!isConfirmed" class="confirm-modal">
      <div class="confirm-content">
        <h3>✨ 溫馨小提醒 ✨</h3>
        <div class="confirm-items">
          <p>💡 請輸入您的訂單編號以及電子信箱以取得您的 eSIM 行動方案。</p>
          <p>💡 需於下單 30 天內自行兌換，逾期未兌換行動方案將失效。</p>
          <p>💡 兌換後收到 eSIM QRcode，請在 60 天內加入您的手機並使用，否則行動方案將失效。</p>
          <p>💡 若您單筆訂單購買多組 eSIM，兌換時將一併寄出，全部需於【60 天內使用】。</p>
        </div>
        <button @click="confirmAndProceed" class="confirm-btn">
          了解了！開始兌換 eSIM ❤️
        </button>
      </div>
    </div>

    <!-- 主要表單內容 -->
    <template v-if="isConfirmed">
      <div class="floating-elements">
        <div class="floating-bubble bubble1"></div>
        <div class="floating-bubble bubble2"></div>
        <div class="floating-bubble bubble3"></div>
        <div class="floating-bubble bubble4"></div>
        <div class="floating-bubble bubble5"></div>
        <div class="floating-element plane"></div>
        <div class="floating-element star1"></div>
        <div class="floating-element star2"></div>
        <div class="floating-element star3"></div>
      </div>
      
      <div class="esim-form-box">
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label for="orderNumber">✨ 訂單編號 *</label>
            <input 
              type="text" 
              id="orderNumber" 
              v-model="form.orderNumber" 
              required
              placeholder="✏️ 請輸入您的訂單編號"
            >
            <span class="input-focus-effect"></span>
            <span class="error-message" v-if="errors.orderNumber">{{ errors.orderNumber }}</span>
          </div>
          
          <div class="form-group">
            <label for="email">📧 電子信箱 *</label>
            <input 
              type="email" 
              id="email" 
              v-model="form.email" 
              required
              placeholder="✉️ 請輸入您的電子信箱"
              @blur="validateEmail"
            >
            <span class="input-focus-effect"></span>
            <span class="error-message" v-if="errors.email">{{ errors.email }}</span>
          </div>
          
          <div class="form-group">
            <label for="confirmEmail">✨ 再次確認信箱 *</label>
            <input 
              type="email" 
              id="confirmEmail" 
              v-model="form.confirmEmail" 
              required
              placeholder="✉️ 再輸入一次信箱確認一下吧"
              @blur="validateConfirmEmail"
            >
            <span class="input-focus-effect"></span>
            <span class="error-message" v-if="errors.confirmEmail">{{ errors.confirmEmail }}</span>
          </div>
          
          <div class="form-group">
            <label for="receiptTitle">🧾 收據抬頭</label>
            <input 
              type="text" 
              id="receiptTitle" 
              v-model="form.receiptTitle" 
              placeholder="📝 需要收據嗎？填寫抬頭～"
            >
            <span class="input-focus-effect"></span>
          </div>
          
          <div class="form-group">
            <label for="taxId">🏢 統一編號</label>
            <input 
              type="text" 
              id="taxId" 
              v-model="form.taxId" 
              placeholder="🔢 需要統編嗎？填寫在這～"
            >
            <span class="input-focus-effect"></span>
          </div>
          
          <button type="submit" class="submit-btn" :disabled="hasErrors || isSubmitting">
            <span class="btn-text">{{ isSubmitting ? '處理中...' : '立即兌換 eSIM' }}</span>
            <span class="btn-shine"></span>
          </button>
        </form>
        
        <div class="notice">
          <h3>🌟 使用須知 🌟</h3>
          <p>⚠️ 兌換前，請確認手機是否適用及使用時間，完成兌換後恕不退款。</p>
          <p>⚠️ 數位商品收到後無法退換貨，兌換前請詳閱賣場商品內容。</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  data() {
    return {
      isConfirmed: false,
      form: {
        orderNumber: '',
        email: '',
        confirmEmail: '',
        receiptTitle: '',
        taxId: ''
      },
      errors: {
        orderNumber: '',
        email: '',
        confirmEmail: ''
      },
      isSubmitting: false,
      apiResponse: null,
      apiError: null
    }
  },
  computed: {
    hasErrors() {
      return Object.values(this.errors).some(error => error !== '');
    }
  },
  methods: {
    confirmAndProceed() {
      this.isConfirmed = true;
    },
    validateOrderNumber() {
      if (!this.form.orderNumber) {
        this.errors.orderNumber = '訂單編號為必填欄位';
      } else {
        this.errors.orderNumber = '';
      }
    },
    
    validateEmail() {
      if (!this.form.email) {
        this.errors.email = '電子信箱為必填欄位';
      } else if (!this.isValidEmail(this.form.email)) {
        this.errors.email = '請輸入有效的電子信箱';
      } else {
        this.errors.email = '';
      }
      
      // 如果確認電子信箱已填寫，則驗證兩者是否一致
      if (this.form.confirmEmail) {
        this.validateConfirmEmail();
      }
    },
    
    validateConfirmEmail() {
      if (!this.form.confirmEmail) {
        this.errors.confirmEmail = '請再次輸入電子信箱';
      } else if (!this.isValidEmail(this.form.confirmEmail)) {
        this.errors.confirmEmail = '請輸入有效的電子信箱';
      } else if (this.form.email !== this.form.confirmEmail) {
        this.errors.confirmEmail = '兩次輸入的電子信箱不一致';
      } else {
        this.errors.confirmEmail = '';
      }
    },
    
    isValidEmail(email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    },
    
    validateForm() {
      this.validateOrderNumber();
      this.validateEmail();
      this.validateConfirmEmail();
      return !this.hasErrors;
    },
    
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }
      
      this.isSubmitting = true;
      this.apiResponse = null;
      this.apiError = null;
      
      try {
        const response = await fetch('https://esim-backend.flynetworld.com/order/confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            orderNumber: this.form.orderNumber,
            email: this.form.email,
            receiptTitle: this.form.receiptTitle,
            taxId: this.form.taxId
          })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.message || '提交失敗，請稍後再試');
        }
        
        this.apiResponse = data;
        this.showSuccessMessage();
        
      } catch (error) {
        console.error('API 錯誤:', error);
        this.apiError = error.message || '網絡請求失敗，請檢查您的網絡連接';
        this.showErrorMessage();
      } finally {
        this.isSubmitting = false;
      }
    },
    
    showSuccessMessage() {
      alert('eSIM 兌換成功！請檢查您的電子郵件以獲取詳細信息。');
    },
    
    showErrorMessage() {
      alert(`提交失敗: ${this.apiError}`);
    }
  }
}
</script>

<style>
/* 基礎樣式 - 適用於電腦版 */
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
}

body {
  background: url('./assets/background.gif') no-repeat top center;
  background-size: cover;
  background-attachment: fixed;
}

#app {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.esim-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

/* 手機版特別處理 */
@media screen and (max-width: 768px) {
  html {
    height: -webkit-fill-available;
  }

  body {
    min-height: 100vh;
    min-height: -webkit-fill-available;
    background-attachment: scroll;
    background-position: top center; /* 確保手機版也是頂部對齊 */
    -webkit-overflow-scrolling: touch;
  }

  #app {
    min-height: 100vh;
    min-height: -webkit-fill-available;
    display: block;
  }

  .esim-container {
    min-height: 100vh;
    min-height: -webkit-fill-available;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start; /* 改為頂部對齊 */
    height: auto;
  }

  .esim-form-box {
    width: 100%;
    margin: 16px 0;
    padding: 16px;
    border-radius: 12px;
    background-color: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    box-sizing: border-box;
    position: relative;
  }
}

/* iOS Safari 特殊處理 */
@supports (-webkit-touch-callout: none) {
  @media screen and (max-width: 768px) {
    .esim-container {
      padding-top: max(16px, env(safe-area-inset-top));
      padding-bottom: max(16px, env(safe-area-inset-bottom));
    }
  }
}

/* 處理較小螢幕 */
@media screen and (max-width: 768px) and (max-height: 700px) {
  .esim-container {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .esim-form-box {
    margin: 8px 0;
  }

  .form-group {
    margin-bottom: 12px;
  }
}

.esim-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 8vh;
}

.esim-form-box {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 25px;
  box-shadow: 0 10px 30px rgba(255, 182, 193, 0.25);
  border: 3px solid rgba(255, 182, 193, 0.3);
  overflow: hidden;
  padding: 20px;  /* 減少電腦版的內邊距 */
  width: 450px;   /* 稍微縮小寬度 */
}

form {
  text-align: left;
}

.form-group {
  margin-bottom: 12px;
  position: relative;
}

label {
  display: block;
  margin-bottom: 4px;
  font-weight: 600;
  color: #ff6b8b;
  font-size: 13px;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
}

input {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid rgba(255, 182, 193, 0.3);
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: 15px;
  font-size: 14px;
  box-sizing: border-box;
  transition: all 0.3s ease;
}

input:focus {
  border-color: #ff6b8b;
  background-color: rgba(255, 255, 255, 0.95);
  outline: none;
  box-shadow: 0 0 12px rgba(255, 107, 139, 0.3);
  transform: translateY(-2px);
}

.error-message {
  color: #ff4d6d;
  font-size: 13px;
  display: block;
  margin-top: 5px;
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shake {
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-3px, 0, 0); }
  40%, 60% { transform: translate3d(3px, 0, 0); }
}

.submit-btn {
  background: linear-gradient(45deg, #ff6b8b, #ff9eb5);
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 18px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  width: 100%;
  margin-top: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(255, 107, 139, 0.3);
}

.submit-btn:hover {
  background: linear-gradient(45deg, #ff5c7f, #ff8fa8);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 7px 15px rgba(255, 107, 139, 0.4);
}

.submit-btn:active {
  transform: translateY(1px);
  box-shadow: 0 2px 5px rgba(255, 107, 139, 0.4);
}

.notice {
  margin-top: 15px;
  text-align: left;
  padding: 12px;
  background: rgba(255, 182, 193, 0.1);
  border-radius: 15px;
  border: 2px solid rgba(255, 182, 193, 0.2);
  transition: all 0.3s ease;
}

.notice h3 {
  color: #ff6b8b;
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 1px;
}

.notice p {
  margin: 6px 0;
  font-size: 12px;
  line-height: 1.4;
  position: relative;
  padding-left: 5px;
  transition: transform 0.2s ease;
}

.notice p:hover {
  transform: translateX(3px);
}

/* 確保整頁顯示，無需拖拉 */
@media screen and (max-height: 700px) {
  .esim-form-box {
    padding: 15px;
    max-height: 90vh;
  }
  
  .form-group {
    margin-bottom: 8px;
  }
  
  .notice p {
    margin: 4px 0;
    font-size: 12px;
  }
  
  label {
    font-size: 13px;
    margin-bottom: 3px;
  }
  
  input {
    padding: 6px 8px;
    font-size: 14px;
  }
}

/* 頁面載入動畫 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.esim-form-box {
  /* ... existing styles ... */
  animation: fadeInUp 0.8s ease-out forwards, float 6s ease-in-out infinite;
  animation-delay: 0.2s;
  opacity: 0;
}

/* 表單內元素逐個顯示 */
.form-group {
  /* ... existing styles ... */
  animation: fadeInUp 0.5s ease-out forwards;
  opacity: 0;
}

.form-group:nth-child(1) { animation-delay: 0.4s; }
.form-group:nth-child(2) { animation-delay: 0.5s; }
.form-group:nth-child(3) { animation-delay: 0.6s; }
.form-group:nth-child(4) { animation-delay: 0.7s; }
.form-group:nth-child(5) { animation-delay: 0.8s; }

.submit-btn {
  /* ... existing styles ... */
  animation: fadeInUp 0.5s ease-out forwards;
  animation-delay: 0.9s;
  opacity: 0;
  position: relative;
  overflow: hidden;
}

.btn-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.3) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: rotate(30deg);
  animation: shineEffect 4s infinite;
}

@keyframes shineEffect {
  0% { left: -150%; }
  20% { left: 100%; }
  100% { left: 100%; }
}

.notice {
  /* ... existing styles ... */
  animation: fadeInUp 0.5s ease-out forwards;
  animation-delay: 1s;
  opacity: 0;
}

/* 背景動畫元素 */
.floating-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
}

.floating-bubble {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(255, 255, 255, 0.2), rgba(255, 182, 193, 0.3));
  backdrop-filter: blur(1px);
  box-shadow: 0 2px 10px rgba(255, 182, 193, 0.2);
  animation: float-random 12s infinite ease-in-out;
}

.bubble1 {
  width: 80px;
  height: 80px;
  left: 10%;
  top: 20%;
  animation-duration: 15s;
}

.bubble2 {
  width: 40px;
  height: 40px;
  right: 15%;
  top: 30%;
  animation-duration: 18s;
  animation-delay: 1s;
}

.bubble3 {
  width: 60px;
  height: 60px;
  left: 20%;
  bottom: 25%;
  animation-duration: 16s;
  animation-delay: 2s;
}

.bubble4 {
  width: 30px;
  height: 30px;
  right: 25%;
  bottom: 15%;
  animation-duration: 12s;
  animation-delay: 3s;
}

.bubble5 {
  display: none;
}

@keyframes float-random {
  0%, 100% {
    transform: translateY(0) translateX(0);
  }
  25% {
    transform: translateY(-20px) translateX(10px);
  }
  50% {
    transform: translateY(10px) translateX(20px);
  }
  75% {
    transform: translateY(15px) translateX(-10px);
  }
}

.floating-element {
  position: absolute;
  pointer-events: none;
  animation: float-rotate 15s infinite ease-in-out;
}

.plane {
  width: 40px;
  height: 40px;
  right: 10%;
  top: 30%;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ff6b8b"><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>') no-repeat center center;
  animation: random-flight 30s infinite ease-in-out;
  transform-origin: center center;
}

.star1, .star2, .star3 {
  width: 20px;
  height: 20px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ffcc80"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>') no-repeat center center;
}

.star1 {
  left: 15%;
  top: 40%;
  animation-duration: 20s;
}

.star2 {
  right: 20%;
  bottom: 30%;
  animation-duration: 22s;
  animation-delay: 2s;
}

.star3 {
  left: 30%;
  bottom: 20%;
  animation-duration: 18s;
  animation-delay: 4s;
}

@keyframes float-rotate {
  0% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-30px) rotate(5deg);
  }
  50% {
    transform: translateY(0) rotate(0deg);
  }
  75% {
    transform: translateY(30px) rotate(-5deg);
  }
  100% {
    transform: translateY(0) rotate(0deg);
  }
}

@keyframes random-flight {
  0% {
    transform: translate(0, 0) rotate(0deg);
  }
  10% {
    transform: translate(-100px, -50px) rotate(-15deg);
  }
  20% {
    transform: translate(-150px, 70px) rotate(10deg);
  }
  30% {
    transform: translate(-50px, 120px) rotate(25deg);
  }
  40% {
    transform: translate(100px, 50px) rotate(-5deg);
  }
  50% {
    transform: translate(150px, -80px) rotate(-20deg);
  }
  60% {
    transform: translate(80px, -150px) rotate(15deg);
  }
  70% {
    transform: translate(-60px, -100px) rotate(30deg);
  }
  80% {
    transform: translate(-120px, 20px) rotate(-10deg);
  }
  90% {
    transform: translate(30px, 100px) rotate(5deg);
  }
  100% {
    transform: translate(0, 0) rotate(0deg);
  }
}

/* 輸入框聚焦效果 */
.form-group {
  position: relative;
}

.input-focus-effect {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #ff6b8b, #ff9eb5);
  transition: width 0.4s ease;
}

.form-group input:focus + .input-focus-effect {
  width: 100%;
}

/* 提升表單層級 */
.esim-form-box {
  position: relative;
  z-index: 2;
}

/* 所有動畫的統一持續時間 */
* {
  animation-fill-mode: both;
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation: none !important;
    transition: none !important;
  }
}

/* 禁用按鈕樣式 */
.submit-btn:disabled {
  background: linear-gradient(45deg, #cbcbcb, #e2e2e2);
  cursor: not-allowed;
  box-shadow: none;
}

.submit-btn:disabled:hover {
  transform: none;
  box-shadow: none;
}

/* 響應式設計調整 */
@media screen and (max-width: 768px) {
  .esim-container {
    padding-bottom: 0;
    align-items: center;
  }

  .esim-form-box {
    width: 90%;
    margin-top: 80px;
    padding: 15px;
  }

  .form-group {
    margin-bottom: 12px;
  }

  label {
    font-size: 14px;
  }

  input {
    padding: 12px;
    font-size: 16px; /* 提高手機上的輸入框字體大小，避免自動放大 */
  }

  .submit-btn {
    padding: 14px;
    font-size: 16px;
    margin-top: 15px;
  }

  .notice {
    padding: 10px;
    margin-top: 12px;
  }

  .notice h3 {
    font-size: 14px;
  }

  .notice p {
    font-size: 13px;
    line-height: 1.5;
    margin: 8px 0;
  }
}

/* 針對特小螢幕的調整 */
@media screen and (max-width: 320px) {
  .esim-form-box {
    width: 95%;
    padding: 12px;
  }

  input {
    padding: 10px;
  }

  .notice p {
    font-size: 12px;
  }
}

/* 處理橫向模式 */
@media screen and (max-height: 500px) {
  .esim-container {
    align-items: flex-start;
    padding-top: 10px;
  }

  .esim-form-box {
    max-height: 95vh;
  }
}

/* 優化背景元素在手機上的顯示 */
@media screen and (max-width: 768px) {
  .floating-bubble {
    display: none; /* 在手機版隱藏部分裝飾元素以提升效能 */
  }
  
  .floating-element {
    transform: scale(0.7); /* 縮小裝飾元素 */
  }
  
  body {
    background-size: cover; /* 調整背景圖片顯示方式 */
    background-position: center center;
  }
}

/* 平板直向模式 (768px - 1024px) */
@media screen and (min-width: 768px) and (max-width: 1024px) {
  .esim-container {
    padding: 20px 15px;
  }

  .esim-form-box {
    width: 85%;
    max-width: 700px;
    padding: 25px;
    margin: 15px auto;
  }

  form {
    display: flex;
    flex-direction: column;
    align-items: center; /* 確保表單內容置中 */
  }

  .form-group {
    margin-bottom: 20px;
    width: 100%; /* 確保表單群組佔滿寬度 */
  }

  label {
    font-size: 16px;
    margin-bottom: 8px;
  }

  input {
    padding: 14px;
    font-size: 16px;
    width: 100%;
  }

  .submit-btn {
    padding: 16px;
    font-size: 18px;
    width: 60%; /* 調整按鈕寬度 */
    max-width: 400px;
    margin: 25px auto; /* 使用 auto 來置中 */
    display: block; /* 確保按鈕可以被 margin: auto 置中 */
  }

  .notice {
    padding: 20px;
    margin-top: 20px;
    width: 100%;
  }
}

/* 平板橫向模式 (1024px - 1366px) */
@media screen and (min-width: 1024px) and (max-width: 1366px) {
  .esim-container {
    padding: 30px 20px;
  }

  .esim-form-box {
    width: 75%;
    max-width: 900px;
    padding: 30px;
  }

  .form-group {
    margin-bottom: 25px;
  }

  /* 在較寬的螢幕上使用兩欄布局 */
  .form-row {
    display: flex;
    gap: 20px;
  }

  .form-row .form-group {
    flex: 1;
  }

  .submit-btn {
    width: 60%;
    max-width: 500px;
    margin: 30px auto; /* 確保橫向模式也置中 */
    display: block;
  }
}

/* 優化平板上的背景元素和動畫 */
@media screen and (min-width: 768px) and (max-width: 1366px) {
  .floating-element {
    transform: scale(0.85);
  }
  
  .floating-bubble {
    opacity: 0.8;
  }
  
  /* 優化觸控體驗 */
  input, 
  select, 
  .submit-btn {
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* 改善滾動效果 */
  .esim-form-box {
    -webkit-overflow-scrolling: touch;
    overflow-y: auto;
  }
}

/* 確認視窗樣式 */
.confirm-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 192, 203, 0.15);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.confirm-content {
  background: rgba(255, 255, 255, 0.95);
  padding: 30px;
  border-radius: 25px;
  max-width: 90%;
  width: 400px;
  box-shadow: 0 10px 30px rgba(255, 182, 193, 0.3);
  border: 3px solid rgba(255, 182, 193, 0.5);
  animation: modalFadeIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.confirm-content h3 {
  color: #ff6b8b;
  margin: 0 0 20px 0;
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 1px;
}

.confirm-items {
  margin-bottom: 20px;
}

.confirm-items p {
  margin: 12px 0;
  font-size: 15px;
  line-height: 1.6;
  color: #666;
  padding: 8px 15px;
  background: rgba(255, 182, 193, 0.1);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.confirm-items p:hover {
  background: rgba(255, 182, 193, 0.2);
  transform: translateX(5px);
}

.confirm-btn {
  background: linear-gradient(45deg, #ff6b8b, #ff9eb5);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 50px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  width: 100%;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(255, 107, 139, 0.3);
  margin-top: 20px;
}

.confirm-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(255, 107, 139, 0.4);
  background: linear-gradient(45deg, #ff5c7f, #ff8fa8);
}

/* 手機版適配 */
@media screen and (max-width: 768px) {
  .confirm-content {
    padding: 25px;
    margin: 15px;
  }

  .confirm-items p {
    font-size: 14px;
    padding: 6px 12px;
  }

  .notice {
    padding: 15px;
  }

  .notice p {
    font-size: 13px;
    margin: 8px 0;
  }
}

/* 手機版隱藏表單標籤 */
@media screen and (max-width: 768px) {
  .form-group label {
    display: none;
  }
  
  /* 調整輸入框樣式，移除上方空間 */
  .form-group input {
    margin-top: 0;
  }
  
  /* 如果有使用 placeholder，可以調整其樣式 */
  .form-group input::placeholder {
    color: #666;
    font-size: 14px;
  }
}

/* 調整手機版樣式 */
@media screen and (max-width: 768px) {
  /* 已存在的手機版樣式 */
  .esim-form-box {
    width: 90%;
    margin-top: 180px;  /* 將表單往下移動更多（原本是 40px） */
    padding: 15px;
  }

  .form-group {
    margin-bottom: 8px;  /* 大幅減少表單項目間的間距 */
  }

  .form-group input {
    padding: 8px 12px;  /* 減少輸入框的內邊距 */
    font-size: 14px;    /* 調整輸入框字體大小 */
    height: 40px;       /* 減少輸入框高度 */
  }

  .notice {
    margin-top: 10px;   /* 減少注意事項的上方間距 */
    padding: 12px;      /* 減少注意事項的內邊距 */
  }

  .notice h3 {
    margin-bottom: 8px; /* 減少標題下方間距 */
    font-size: 15px;    /* 調整標題大小 */
  }

  .notice p {
    margin: 5px 0;      /* 減少注意事項段落間的間距 */
    font-size: 13px;    /* 稍微縮小字體 */
    line-height: 1.4;   /* 調整行高 */
  }

  /* 提交按鈕調整 */
  button[type="submit"] {
    margin-top: 10px;   /* 減少按鈕上方間距 */
    padding: 10px 20px; /* 減少按鈕內邊距 */
    height: 40px;       /* 設定按鈕高度 */
  }
}

/* 動畫效果 */
@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
